import { extendTheme, type ThemeConfig } from '@chakra-ui/react'
import { tabsAnatomy } from '@chakra-ui/anatomy'
import { createMultiStyleConfigHelpers } from '@chakra-ui/styled-system'

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(
  tabsAnatomy.keys
)

const config: ThemeConfig = {
  initialColorMode: 'dark',
  useSystemColorMode: true,
}

const colors = {
  admin: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },
  brand: {
    50: '#fff5ee',
    100: '#fed7aa',
    200: '#fdba74',
    300: '#fb923c',
    400: '#f97316',
    500: '#ea580c',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
}

const fonts = {
  heading:
    'system-ui, -apple-system, BlinkMacSystemFont, \'Segoe UI\', ' +
    '\'Helvetica Neue\', Arial, sans-serif',
  body:
    'system-ui, -apple-system, BlinkMacSystemFont, \'Segoe UI\', ' +
    '\'Helvetica Neue\', Arial, sans-serif',
}

const tabsBaseStyle = definePartsStyle((props) => ({
  tablist: {
    gap: 2,
  },
  tab: {
    position: 'relative',
    minH: '44px',
    px: 4,
    py: 2.5,
    borderRadius: 'lg',
    color: props.colorMode === 'dark' ? 'gray.300' : 'gray.600',
    fontWeight: 'semibold',
    transitionProperty: 'common',
    transitionDuration: '180ms',
    _hover: {
      bg: props.colorMode === 'dark' ? 'whiteAlpha.120' : 'blackAlpha.50',
      color: props.colorMode === 'dark' ? 'white' : 'gray.900',
    },
    _focus: {
      boxShadow: 'none',
      outline: 'none',
    },
    _focusVisible: {
      boxShadow: 'none',
      outline: 'none',
    },
  },
  tabpanel: {
    px: 0,
    pt: 5,
  },
}))

const tabsEnclosedVariant = definePartsStyle((props) => {
  const scheme = props.colorScheme ?? 'admin'
  return {
    tablist: {
      p: 1.5,
      borderWidth: '1px',
      borderColor: props.colorMode === 'dark' ? 'whiteAlpha.200' : 'blackAlpha.100',
      borderRadius: 'xl',
      bg: props.colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50',
      boxShadow: props.colorMode === 'dark' ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    },
    tab: {
      _selected: {
        borderWidth: '1px',
        bg: props.colorMode === 'dark' ? 'gray.700' : 'white',
        color: props.colorMode === 'dark' ? `${scheme}.200` : `${scheme}.700`,
        borderColor: props.colorMode === 'dark' ? 'whiteAlpha.200' : `${scheme}.200`,
        boxShadow: props.colorMode === 'dark'
          ? '0 10px 24px rgba(0, 0, 0, 0.28)'
          : '0 8px 20px rgba(14, 165, 233, 0.12)',
      },
    },
  }
})

const tabsLineVariant = definePartsStyle((props) => {
  const scheme = props.colorScheme ?? 'admin'
  return {
    tablist: {
      borderBottomWidth: '1px',
      borderBottomColor: props.colorMode === 'dark' ? 'whiteAlpha.200' : 'blackAlpha.100',
      gap: 2,
    },
    tab: {
      mb: '-1px',
      borderBottomWidth: '3px',
      borderBottomColor: 'transparent',
      borderRadius: 'lg lg 0 0',
      _selected: {
        color: props.colorMode === 'dark' ? `${scheme}.200` : `${scheme}.700`,
        borderBottomColor: `${scheme}.500`,
        bg: props.colorMode === 'dark' ? 'whiteAlpha.50' : `${scheme}.50`,
      },
    },
  }
})

const components = {
  Button: {
    defaultProps: {
      colorScheme: 'admin',
    },
    variants: {
      solid: {
        bg: 'admin.600',
        color: 'white',
        _hover: {
          bg: 'admin.700',
        },
      },
    },
  },
  Card: {
    baseStyle: {
      container: {
        borderRadius: 'lg',
        boxShadow: 'lg',
      },
    },
  },
  Sidebar: {
    baseStyle: {
      bg: 'gray.800',
      color: 'white',
    },
  },
  Skeleton: {
    baseStyle: {
      borderRadius: 'md',
    },
  },
  Tabs: defineMultiStyleConfig({
    baseStyle: tabsBaseStyle,
    variants: {
      enclosed: tabsEnclosedVariant,
      line: tabsLineVariant,
    },
    defaultProps: {
      colorScheme: 'admin',
    },
  }),
}

export const theme = extendTheme({
  config,
  colors,
  fonts,
  components,
  styles: {
    global: (props: any) => ({
      'html, body, #root': {
        minHeight: '100%',
        backgroundColor: props.colorMode === 'dark' ? 'gray.900' : 'gray.50',
      },
      body: {
        bg: props.colorMode === 'dark' ? 'gray.900' : 'gray.50',
        color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
      },
      // Improve table styling in dark mode
      'th, td': {
        borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
      },
      // Improve divider styling in dark mode
      hr: {
        borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
      },
      '@media (prefers-reduced-motion: reduce)': {
        '.chakra-skeleton': {
          animation: 'none !important',
        },
      },
    }),
  },
})
